export function resolveSeedePrompt(content) {
  if (typeof content === 'string') return content;
  if (Array.isArray(content)) {
    // Convert array of content objects to a string
    return content
      .map((item) => {
        if (item.type === 'text') {
          return item.text;
        } else if (item.type === 'image_url' && item.image_url && item.image_url.url) {
          const url = item.image_url.url.trim();
          return resolveSeedeReferenceImage([
            {
              url,
            },
          ]);
        }
        return '';
      })
      .filter(Boolean)
      .join('\n');
  }
  return '';
}

/**
 * 生成自定义指令 @SeedeContainer，包含 width, height, language 参数
 * @param {Object} options
 * @param {number|string} options.width 宽度
 * @param {number|string} options.height 高度
 * @param {string} options.language 语言
 * @returns {string} 自定义指令
 */
export function resolveSeedeContainer({ width, height, language }) {
  // 参数校验和格式化
  const w = width !== undefined ? width : '';
  const h = height !== undefined ? height : '';
  const lang = language || '';

  // 转义单引号
  const safe = (v) => (typeof v === 'string' ? v.replace(/'/g, "\\'") : v);

  return `@SeedeContainer(width: '${safe(w)}', height: '${safe(h)}', language: '${safe(lang)}')`;
}

export function resolveSeedeReferenceImage(images) {
  if (!images || !images.length) return '';
  const imageContent = images
    .map((image) => {
      const url = typeof image === 'string' ? image : image.url;
      const tag = typeof image === 'object' && image.tag ? image.tag : '';

      // Escape single quotes in url and tag
      const escapedUrl = url.replace(/'/g, "\\'");
      const escapedTag = tag.replace(/'/g, "\\'");

      return `@SeedeReferenceImage(url: '${escapedUrl}', tag: '${escapedTag}')`;
    })
    .join('\n');

  return `
${imageContent}
  `;
}

export function parseSeedeReferenceImage(content) {
  if (!content) return { content: '', images: [] };

  const images = [];

  // First, try to parse JSON format: @SeedeReferenceImage({...})
  const jsonRegex = /@SeedeReferenceImage\((\{.*?\})\)/g;
  let jsonMatch;

  while ((jsonMatch = jsonRegex.exec(content)) !== null) {
    try {
      // Handle potential nested braces by finding matching braces
      let jsonStr = jsonMatch[1];
      let braceCount = 0;
      let endIndex = 0;

      for (let i = 0; i < jsonStr.length; i++) {
        if (jsonStr[i] === '{') braceCount++;
        if (jsonStr[i] === '}') braceCount--;
        if (braceCount === 0) {
          endIndex = i + 1;
          break;
        }
      }

      // If we have unmatched braces, we need to find the complete JSON
      if (braceCount !== 0) {
        const fullMatch = content.slice(jsonMatch.index);
        const startPos = fullMatch.indexOf('{');
        let braces = 0;
        let endPos = -1;

        for (let i = startPos; i < fullMatch.length; i++) {
          if (fullMatch[i] === '{') braces++;
          if (fullMatch[i] === '}') braces--;
          if (braces === 0) {
            endPos = i + 1;
            break;
          }
        }

        if (endPos > 0) {
          jsonStr = fullMatch.slice(startPos, endPos);
        }
      } else {
        jsonStr = jsonStr.slice(0, endIndex);
      }

      const imageData = JSON.parse(jsonStr);
      images.push({
        url: imageData.url,
        tag: imageData.tag || undefined,
        filename: imageData.filename,
        width: imageData.width,
        height: imageData.height,
        aspectRatio: imageData.aspectRatio,
        fnType: imageData.fnType,
      });
    } catch (error) {
      console.error('Failed to parse reference image JSON tag:', error);
    }
  }

  // Remove JSON format tags
  let text = content.replace(/@SeedeReferenceImage\(\{.*?\}\)/g, '');

  // Then, try to parse legacy key-value format: @SeedeReferenceImage(url: '...', tag: '...')
  const kvRegex = /@SeedeReferenceImage\(url:\s*'([^']*)',\s*tag:\s*'([^']*)'\)/g;
  let kvMatch;

  while ((kvMatch = kvRegex.exec(text)) !== null) {
    const url = kvMatch[1].replace(/\\'/g, "'"); // Unescape single quotes
    const tag = kvMatch[2].replace(/\\'/g, "'"); // Unescape single quotes
    images.push({
      url,
      tag: tag || undefined,
    });
  }

  // Remove the key-value format reference image tags to get clean text
  text = text.replace(kvRegex, '');

  if (!images.length) {
    return parseSeedeReferenceImageLegacy(text);
  }

  // Remove SeedeReferenceImageLegacy tags from content
  const legacyImageRegex = /\[SeedeImage##([^\]]+)\]/g;
  text = text.replace(legacyImageRegex, '');

  return {
    content: text,
    images,
  };
}

export function parseSeedeReferenceImageLegacy(content) {
  if (!content) return { content: '', images: [] };

  // Extract image URLs from the content
  const imageRegex = /\[SeedeImage##([^\]]+)\]/g;
  const images = [];
  let match;

  while ((match = imageRegex.exec(content)) !== null) {
    images.push({
      url: match[1],
      tag: '',
    });
  }

  // Remove the image tags to get clean text
  const text = content.replace(imageRegex, '');

  return {
    content: text,
    images,
  };
}

export function parseSeedeTheme(content) {
  if (!content) return { content: '', theme: null };

  // Extract theme data from the content
  const themeRegex = /@SeedeTheme\(([^)]+)\)/g;
  let theme = null;
  let match;

  while ((match = themeRegex.exec(content)) !== null) {
    try {
      const themeData = JSON.parse(match[1]);
      theme = {
        value: themeData.value,
        label: themeData.label,
        description: themeData.description,
        colors: themeData.colors,
        primaryColor: themeData.primaryColor,
        secondaryColor: themeData.secondaryColor,
      };
      // Only use the first theme found
      break;
    } catch (error) {
      console.error('Failed to parse theme tag:', error);
    }
  }

  // Remove the theme tags to get clean text
  const text = content.replace(themeRegex, '');

  return {
    content: text,
    theme,
  };
}

export function parseSeedeDocument(content) {
  if (!content) return { content: '', documents: [] };

  // Extract document tags from the content
  // Use a more robust regex that handles nested parentheses in JSON content
  const documentRegex = /@SeedeDocument\((\{.*?\})\)/g;
  const documents = [];
  let documentMatch;

  while ((documentMatch = documentRegex.exec(content)) !== null) {
    try {
      // Handle potential nested parentheses by finding matching braces
      let jsonStr = documentMatch[1];
      let braceCount = 0;
      let endIndex = 0;

      for (let i = 0; i < jsonStr.length; i++) {
        if (jsonStr[i] === '{') braceCount++;
        if (jsonStr[i] === '}') braceCount--;
        if (braceCount === 0) {
          endIndex = i + 1;
          break;
        }
      }

      // If we have unmatched braces, we need to find the complete JSON
      if (braceCount !== 0) {
        // Look for the complete JSON string by finding the closing brace
        const fullMatch = content.slice(documentMatch.index);
        const startPos = fullMatch.indexOf('{');
        let braces = 0;
        let endPos = -1;

        for (let i = startPos; i < fullMatch.length; i++) {
          if (fullMatch[i] === '{') braces++;
          if (fullMatch[i] === '}') braces--;
          if (braces === 0) {
            endPos = i + 1;
            break;
          }
        }

        if (endPos > 0) {
          jsonStr = fullMatch.slice(startPos, endPos);
        }
      } else {
        jsonStr = jsonStr.slice(0, endIndex);
      }

      const documentData = JSON.parse(jsonStr);
      documents.push({
        filename: documentData.filename || documentData.name,
        id: documentData.id,
      });
    } catch (error) {
      console.error('Failed to parse document tag:', error);
    }
  }

  // Remove the document tags to get clean text
  const text = content.replace(/@SeedeDocument\(\{.*?\}\)/g, '');

  return {
    content: text,
    documents,
  };
}

export function parseSeedeMaterial(content, { replace = true } = {}) {
  if (!content) return { content: '', materials: [] };

  // Extract material tags from the content
  // Use a more robust regex that handles nested parentheses in JSON content
  const materialRegex = /@SeedeMaterial\((\{.*?\})\)/g;
  const materials = [];
  let materialMatch;

  while ((materialMatch = materialRegex.exec(content)) !== null) {
    try {
      // Handle potential nested parentheses by finding matching braces
      let jsonStr = materialMatch[1];
      let braceCount = 0;
      let endIndex = 0;

      for (let i = 0; i < jsonStr.length; i++) {
        if (jsonStr[i] === '{') braceCount++;
        if (jsonStr[i] === '}') braceCount--;
        if (braceCount === 0) {
          endIndex = i + 1;
          break;
        }
      }

      // If we have unmatched braces, we need to find the complete JSON
      if (braceCount !== 0) {
        // Look for the complete JSON string by finding the closing brace
        const fullMatch = content.slice(materialMatch.index);
        const startPos = fullMatch.indexOf('{');
        let braces = 0;
        let endPos = -1;

        for (let i = startPos; i < fullMatch.length; i++) {
          if (fullMatch[i] === '{') braces++;
          if (fullMatch[i] === '}') braces--;
          if (braces === 0) {
            endPos = i + 1;
            break;
          }
        }

        if (endPos > 0) {
          jsonStr = fullMatch.slice(startPos, endPos);
        }
      } else {
        jsonStr = jsonStr.slice(0, endIndex);
      }

      const materialData = JSON.parse(jsonStr);
      materials.push({
        id: materialData.id,
        filename: materialData.filename,
        url: materialData.url,
        width: materialData.width,
        height: materialData.height,
        aspectRatio: materialData.aspectRatio,
        tag: materialData.tag,
      });
    } catch (error) {
      console.error('Failed to parse material tag:', error);
    }
  }

  // Remove the material tags to get clean text
  const text = replace ? content.replace(/@SeedeMaterial\(\{.*?\}\)/g, '') : content;

  return {
    content: text,
    materials,
  };
}

export function parseSeedePrompt(content, { replaceMaterial = true } = {}) {
  if (!content) return { text: '', images: [], materials: [], theme: null, documents: [] };

  const themePayload = parseSeedeTheme(content);
  content = themePayload.content;

  const documentPayload = parseSeedeDocument(content);
  content = documentPayload.content;

  const materialPayload = parseSeedeMaterial(content, { replace: replaceMaterial });
  content = materialPayload.content;

  const stylePayload = parseSeedeStyle(content);
  content = stylePayload.content;

  const referenceImagePayload = parseSeedeReferenceImage(content);

  // Remove the image tags to get clean text
  const text = referenceImagePayload.content;

  return {
    text,
    images: referenceImagePayload.images,
    materials: materialPayload.materials,
    theme: themePayload.theme,
    style: stylePayload.style,
    documents: documentPayload.documents.map((asset) => {
      asset.parsed = true;
      return asset;
    }),
  };
}

export function resolveAssetsContent(assets) {
  if (!assets || !assets.length) return '';
  const assetsContent = assets
    .map((asset) => {
      const w = asset.meta?.w || asset.width || 128;
      const h = asset.meta?.h || asset.height || 128;
      // Remove newline characters from filename
      let safeFilename = (asset.name || '').replace(/[\r\n]+/g, ' ');
      return `
  @SeedeMaterial(${JSON.stringify({
    id: asset.id || '',
    filename: safeFilename,
    url: asset.url,
    width: w,
    height: h,
    aspectRatio: Math.round((100 * w) / h) / 100,
    tag: asset.tag || '',
  })})`;
    })
    .join('\n');

  return `
  ${assetsContent}
    `;
}

export function resolveDocumentContent(documents) {
  if (!documents || !documents.length) return '';
  const documentContent = documents
    .map((asset) => {
      return `
  @SeedeDocument(${JSON.stringify({
    filename: asset.filename || asset.name,
    id: asset.id,
  })})`;
    })
    .join('\n');

  return `
  ${documentContent}
    `;
}

export function resolveSeedeStyle(value, label) {
  const escapedValue = value.replace(/'/g, "\\'");
  const escapedLabel = label.replace(/'/g, "\\'");
  return `SeedeStyle('${escapedValue}', '${escapedLabel}')`;
}

export function parseSeedeStyle(content) {
  if (!content) return { content: '', style: null };

  // Extract style data from the content
  const styleRegex = /SeedeStyle\('([^']+)',\s*'([^']+)'\)/g;
  let style = null;
  let match;

  while ((match = styleRegex.exec(content)) !== null) {
    const value = match[1].replace(/\\'/g, "'");
    const label = match[2].replace(/\\'/g, "'");
    style = {
      value,
      label,
    };
    // Only use the first style found
    break;
  }

  // Remove the style tags to get clean text
  const text = content.replace(styleRegex, '');

  return {
    content: text,
    style,
  };
}
